<template>
  <div class="special-label-admin">
    <div class="header">
      <div>
        <h2>Special Labels</h2>
        <p>Reusable awards, editor’s choices, and collection highlights.</p>
      </div>
      <el-button type="primary" @click="open()">Create Label</el-button>
    </div>
    <el-alert
      v-if="loadError"
      :title="loadError"
      type="error"
      :closable="false"
    />
    <el-button size="small" :loading="loading" @click="load">Refresh</el-button>
    <el-table
      v-loading="loading"
      :data="items"
      empty-text="No special labels yet. Create your first label."
      style="width: 100%"
    >
      <el-table-column label="Banner" width="176"
        ><template slot-scope="{ row }"
          ><img
            :src="row.image_url"
            :alt="row.name"
            class="preview small" /></template
      ></el-table-column>
      <el-table-column prop="name" label="Label Name" min-width="160" />
      <el-table-column
        prop="description"
        label="Description"
        min-width="220"
        show-overflow-tooltip
      />
      <el-table-column label="Hyperlink" min-width="190"
        ><template slot-scope="{ row }"
          ><a
            :href="safeUrl(row.hyperlink)"
            target="_blank"
            rel="noopener noreferrer"
            >{{ row.hyperlink }}</a
          ></template
        ></el-table-column
      >
      <el-table-column label="Actions" width="160"
        ><template slot-scope="{ row }"
          ><el-button size="mini" @click="open(row)">Edit</el-button
          ><el-button size="mini" type="danger" @click="remove(row)"
            >Delete</el-button
          ></template
        ></el-table-column
      >
    </el-table>
    <el-dialog
      :title="form.id ? 'Edit Label' : 'Create Label'"
      :visible.sync="visible"
      width="min(600px, 94vw)"
      :close-on-click-modal="false"
      :before-close="close"
    >
      <el-form
        label-position="top"
        :model="form"
        :disabled="saving || uploading"
      >
        <el-form-item label="Label Name" required
          ><el-input
            v-model="form.name"
            aria-label="Label Name"
            maxlength="120"
            show-word-limit
        /></el-form-item>
        <el-form-item label="Hyperlink" required
          ><el-input
            v-model="form.hyperlink"
            aria-label="Hyperlink"
            placeholder="https://example.com/award"
        /></el-form-item>
        <el-form-item label="Detail Description" required
          ><el-input
            v-model="form.description"
            aria-label="Detail Description"
            type="textarea"
            :rows="4"
            maxlength="4000"
            show-word-limit
        /></el-form-item>
        <el-form-item label="Banner Image" required>
          <img
            v-if="form.image_url"
            :src="form.image_url"
            alt="Label preview"
            class="preview"
          />
          <el-upload
            action=""
            accept="image/png,image/jpeg,image/webp"
            :show-file-list="false"
            :before-upload="upload"
            :disabled="uploading || saving"
            ><el-button :loading="uploading">{{
              form.image_url ? 'Replace Banner' : 'Upload Banner'
            }}</el-button></el-upload
          >
          <p class="hint">
            Banner image (4:1), recommended 800 × 200 px. PNG, JPEG or WebP, up
            to 5 MB. Upload a banner in this ratio; it is resized without
            cropping.
          </p>
        </el-form-item>
      </el-form>
      <span slot="footer"
        ><el-button :disabled="saving || uploading" @click="visible = false"
          >Cancel</el-button
        ><el-button
          type="primary"
          :loading="saving"
          :disabled="uploading"
          @click="save"
          >Save Label</el-button
        ></span
      >
    </el-dialog>
  </div>
</template>
<script>
import { postRequest } from '@/apis/lfAdmin';
import * as qiniu from 'qiniu-js';

const blank = () => ({
  id: '',
  name: '',
  hyperlink: '',
  description: '',
  image_url: '',
});
export default {
  name: 'SpecialLabelAdmin',
  data: () => ({
    items: [],
    loading: false,
    loadError: '',
    saving: false,
    uploading: false,
    visible: false,
    form: blank(),
  }),
  mounted() {
    this.load();
  },
  methods: {
    safeUrl(value) {
      try {
        const url = new URL(value);
        return ['https:', 'http:'].includes(url.protocol)
          ? url.href
          : undefined;
      } catch (e) {
        return undefined;
      }
    },
    async request(name, params = {}) {
      const res = await postRequest(name, params);
      if (res.data.code !== 0)
        throw new Error(res.data.msg || 'Request failed');
      return res.data.data;
    },
    async load() {
      this.loading = true;
      this.loadError = '';
      try {
        this.items = (await this.request('listCollectionSpecialLabels')) || [];
      } catch (e) {
        this.loadError = e.message || 'Could not load labels';
      } finally {
        this.loading = false;
      }
    },
    open(row) {
      this.form = row ? { ...row } : blank();
      this.visible = true;
    },
    close(done) {
      if (!this.saving && !this.uploading) done();
    },
    async save() {
      if (
        !this.form.name.trim() ||
        !this.form.description.trim() ||
        !this.form.image_url
      )
        return this.$message.error('Enter a name, description, and image');
      if (!this.safeUrl(this.form.hyperlink))
        return this.$message.error('Enter a valid HTTP or HTTPS hyperlink');
      this.saving = true;
      try {
        await this.request('saveCollectionSpecialLabel', this.form);
        this.visible = false;
        this.$message.success('Label saved');
        await this.load();
      } catch (e) {
        this.$message.error(e.message || 'Could not save label');
      } finally {
        this.saving = false;
      }
    },
    async remove(row) {
      try {
        await this.$confirm(
          `Delete “${row.name}”? This removes it from every collection.`,
          'Delete Label',
          { type: 'warning' }
        );
      } catch (e) {
        return;
      }
      try {
        await this.request('deleteCollectionSpecialLabel', { id: row.id });
        this.$message.success('Label deleted');
        await this.load();
      } catch (e) {
        this.$message.error(e.message || 'Could not delete label');
      }
    },
    async upload(file) {
      if (
        !['image/png', 'image/jpeg', 'image/webp'].includes(file.type) ||
        file.size > 5 * 1024 * 1024
      ) {
        this.$message.error('Choose a PNG, JPEG or WebP image up to 5 MB');
        return false;
      }
      this.uploading = true;
      const objectUrl = URL.createObjectURL(file);
      try {
        const image = new Image();
        await new Promise((resolve, reject) => {
          image.onload = resolve;
          image.onerror = () => reject(new Error('Could not read image'));
          image.src = objectUrl;
        });
        if (image.width !== image.height * 4) {
          throw new Error('Use a 4:1 banner image, for example 800 × 200 px.');
        }
        const canvas = document.createElement('canvas');
        canvas.width = 800;
        canvas.height = 200;
        canvas.getContext('2d').drawImage(image, 0, 0, 800, 200);
        const blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, 'image/png')
        );
        if (!blob) throw new Error('Could not prepare image');
        const token = await this.request('getSingaporeQiniuToken');
        const key = `special-labels/${Date.now()}-${Math.random()
          .toString(36)
          .slice(2)}.png`;
        await new Promise((resolve, reject) =>
          qiniu
            .upload(blob, key, token)
            .subscribe({ complete: resolve, error: reject })
        );
        this.form.image_url = `https://ap.knowit.today/${key}`;
      } catch (e) {
        this.$message.error(e.message || 'Image upload failed; please retry');
      } finally {
        URL.revokeObjectURL(objectUrl);
        this.uploading = false;
      }
      return false;
    },
  },
};
</script>
<style scoped>
.special-label-admin {
  padding: 24px;
}
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}
h2 {
  margin: 0 0 8px;
}
p {
  color: #606266;
}
.preview {
  width: 240px;
  max-width: 100%;
  aspect-ratio: 4 / 1;
  object-fit: contain;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  margin-bottom: 8px;
}
.preview.small {
  width: 144px;
  margin: 0;
}
.hint {
  font-size: 12px;
  line-height: 1.5;
}
a {
  overflow-wrap: anywhere;
}
</style>
